import { useEffect, useMemo, useRef } from 'react';
import { Button, CompassIcon, EmptyState, HelpIcon, Modal, Panel, TextField, cn, localize } from '@spacegame/ui';
import { useTutorial } from './TutorialProvider';
import { CHAPTERS } from './chapters';
import { FAQ } from './faq';
import type { Chapter, FaqEntry } from './types';

const FAQ_CHAPTER_ID = '__faq__';

const COPY = {
  title: { de: 'Hilfe-Center', en: 'Help Center' },
  eyebrow: { de: 'Tutorial & FAQ', en: 'Tutorial & FAQ' },
  search: { de: 'Suche', en: 'Search' },
  searchPlaceholder: { de: 'z. B. „Trümmerfeld", „Talente" …', en: 'e.g. "debris field", "talents" …' },
  faqNav: { de: 'Häufige Fragen', en: 'Frequently asked' },
  tourCta: { de: 'Geführte Tour starten', en: 'Start guided tour' },
  noResults: { de: 'Keine Treffer', en: 'No matches' },
  noResultsHint: {
    de: 'Versuch es mit einem anderen Suchbegriff, oder wähle links ein Kapitel.',
    en: 'Try a different search term, or pick a chapter on the left.',
  },
  languageDe: { de: 'Deutsch', en: 'German' },
  languageEn: { de: 'Englisch', en: 'English' },
} as const;

function matchesQuery(haystack: string[], query: string): boolean {
  const needle = query.toLowerCase();
  return haystack.some((text) => text.toLowerCase().includes(needle));
}

function chapterMatches(chapter: Chapter, query: string, locale: 'de' | 'en'): boolean {
  const haystack = [
    chapter.title[locale],
    chapter.summary[locale],
    ...chapter.sections.flatMap((s) => [s.heading[locale], ...s.body.map((p) => p[locale])]),
  ];
  return matchesQuery(haystack, query);
}

function faqMatches(entry: FaqEntry, query: string, locale: 'de' | 'en'): boolean {
  const haystack = [entry.question[locale], entry.answer[locale], ...(entry.keywords ?? [])];
  return matchesQuery(haystack, query);
}

function ChapterContent({ chapter, locale }: { chapter: Chapter; locale: 'de' | 'en' }) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-display text-sm font-semibold uppercase tracking-[0.1em] text-content">
          {localize(chapter.title, locale)}
        </h3>
        <p className="mt-0.5 text-xs leading-relaxed text-content-secondary">{localize(chapter.summary, locale)}</p>
      </div>
      {chapter.sections.map((section) => (
        <div key={section.id}>
          <h4 className="font-display text-label uppercase tracking-[0.08em] text-content">
            {localize(section.heading, locale)}
          </h4>
          <div className="mt-1.5 space-y-2">
            {section.body.map((paragraph, i) => (
              <p key={i} className="text-xs leading-relaxed text-content-secondary">
                {localize(paragraph, locale)}
              </p>
            ))}
          </div>
          {section.tips?.length ? (
            <div className="mt-2 space-y-1.5">
              {section.tips.map((tip, i) => (
                <div
                  key={i}
                  className="rounded-control border border-silicate/25 bg-silicate/8 px-2.5 py-1.5 text-label-sm leading-relaxed text-silicate-bright"
                >
                  {localize(tip, locale)}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function FaqList({ entries, locale }: { entries: FaqEntry[]; locale: 'de' | 'en' }) {
  return (
    <div className="space-y-3">
      {entries.map((entry) => (
        <Panel key={entry.id} tone="inset">
          <p className="font-display text-label uppercase tracking-[0.06em] text-content">
            {localize(entry.question, locale)}
          </p>
          <p className="mt-1 text-xs leading-relaxed text-content-secondary">{localize(entry.answer, locale)}</p>
        </Panel>
      ))}
    </div>
  );
}

/**
 * The permanent reference work — chapters + FAQ + search — reachable at any
 * time via the Help button, unlike the one-shot guided tour.
 */
export function HelpCenterModal() {
  const tutorial = useTutorial();
  const locale = tutorial.locale;
  const query = tutorial.searchQuery.trim();

  const matchedChapters = useMemo(
    () => (query ? CHAPTERS.filter((chapter) => chapterMatches(chapter, query, locale)) : []),
    [query, locale],
  );
  const matchedFaq = useMemo(
    () => (query ? FAQ.filter((entry) => faqMatches(entry, query, locale)) : []),
    [query, locale],
  );

  const selectedId = tutorial.activeChapterId ?? CHAPTERS[0]!.id;
  const selectedChapter = CHAPTERS.find((c) => c.id === selectedId);

  // Switching chapters shouldn't leave the reader scrolled halfway down the
  // previous one — jump the content pane (and its scrollable Modal ancestor)
  // back to the top of the new chapter.
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!tutorial.isHelpOpen) return;
    // jsdom (unit tests) doesn't implement scrollIntoView.
    contentRef.current?.scrollIntoView?.({ block: 'start' });
  }, [selectedId, tutorial.isHelpOpen]);

  return (
    <Modal
      open={tutorial.isHelpOpen}
      onClose={tutorial.closeHelp}
      title={localize(COPY.title, locale)}
      eyebrow={localize(COPY.eyebrow, locale)}
      size="lg"
      footer={
        <Button variant="secondary" size="sm" icon={<CompassIcon size={15} />} onClick={tutorial.startTour}>
          {localize(COPY.tourCta, locale)}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <TextField
              label={localize(COPY.search, locale)}
              value={tutorial.searchQuery}
              onChange={tutorial.setSearchQuery}
              placeholder={localize(COPY.searchPlaceholder, locale)}
            />
          </div>
          <div className="mt-5 flex items-center gap-1 rounded-control border border-edge bg-surface-3 p-0.5">
            <button
              type="button"
              onClick={() => tutorial.setLocale('de')}
              aria-pressed={locale === 'de'}
              title={localize(COPY.languageDe, locale)}
              className={cn(
                'rounded-[4px] px-2 py-1 font-display text-label-sm uppercase transition-colors',
                locale === 'de' ? 'bg-surface-raised text-content' : 'text-content-muted hover:text-content',
              )}
            >
              DE
            </button>
            <button
              type="button"
              onClick={() => tutorial.setLocale('en')}
              aria-pressed={locale === 'en'}
              title={localize(COPY.languageEn, locale)}
              className={cn(
                'rounded-[4px] px-2 py-1 font-display text-label-sm uppercase transition-colors',
                locale === 'en' ? 'bg-surface-raised text-content' : 'text-content-muted hover:text-content',
              )}
            >
              EN
            </button>
          </div>
        </div>

        {query ? (
          <div className="space-y-4">
            {matchedChapters.length === 0 && matchedFaq.length === 0 ? (
              <EmptyState
                icon={<HelpIcon size={22} />}
                title={localize(COPY.noResults, locale)}
                description={localize(COPY.noResultsHint, locale)}
              />
            ) : (
              <>
                {matchedChapters.map((chapter) => (
                  <div key={chapter.id}>
                    <button
                      type="button"
                      onClick={() => {
                        tutorial.setSearchQuery('');
                        tutorial.setActiveChapterId(chapter.id);
                      }}
                      className="font-display text-label uppercase tracking-[0.08em] text-silicate-bright hover:underline"
                    >
                      {localize(chapter.title, locale)}
                    </button>
                    <p className="mt-1 text-xs leading-relaxed text-content-secondary">
                      {localize(chapter.summary, locale)}
                    </p>
                  </div>
                ))}
                {matchedFaq.length > 0 ? <FaqList entries={matchedFaq} locale={locale} /> : null}
              </>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-4 sm:flex-row">
            <nav className="flex shrink-0 flex-col gap-0.5 sm:w-44">
              {CHAPTERS.map((chapter) => (
                <button
                  key={chapter.id}
                  type="button"
                  onClick={() => tutorial.setActiveChapterId(chapter.id)}
                  className={cn(
                    'rounded-control px-2.5 py-1.5 text-left font-display text-label-sm uppercase tracking-[0.04em] transition-colors',
                    selectedId === chapter.id
                      ? 'bg-silicate/12 text-silicate-bright'
                      : 'text-content-muted hover:bg-surface-3 hover:text-content',
                  )}
                >
                  {localize(chapter.title, locale)}
                </button>
              ))}
              <button
                type="button"
                onClick={() => tutorial.setActiveChapterId(FAQ_CHAPTER_ID)}
                className={cn(
                  'mt-1 rounded-control border-t border-edge-subtle px-2.5 py-1.5 pt-2.5 text-left font-display text-label-sm uppercase tracking-[0.04em] transition-colors',
                  selectedId === FAQ_CHAPTER_ID
                    ? 'bg-silicate/12 text-silicate-bright'
                    : 'text-content-muted hover:bg-surface-3 hover:text-content',
                )}
              >
                {localize(COPY.faqNav, locale)}
              </button>
            </nav>
            <div ref={contentRef} className="min-w-0 flex-1">
              {selectedId === FAQ_CHAPTER_ID ? (
                <FaqList entries={FAQ} locale={locale} />
              ) : selectedChapter ? (
                <ChapterContent chapter={selectedChapter} locale={locale} />
              ) : null}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
